import React from 'react';
import { Share2, Users, Calendar, DollarSign, Wallet } from 'lucide-react';
import { getUserReferralCode, getReferredUsers, type ReferredUser } from '../services/referrals';

interface BalanceSummary {
  totalCommission: number;
  totalWithdrawals: number;
  availableBalance: number;
}

export function ReferralsScreen() {
  const [referralCode, setReferralCode] = React.useState<string | null>(null);
  const [referredUsers, setReferredUsers] = React.useState<ReferredUser[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasConfirmedSales, setHasConfirmedSales] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copySuccess, setCopySuccess] = React.useState(false);
  const [balanceSummary, setBalanceSummary] = React.useState<BalanceSummary>({
    totalCommission: 0,
    totalWithdrawals: 0,
    availableBalance: 0
  });

  React.useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        setError(null);
        
        // Load referral code
        const code = await getUserReferralCode();
        if (!code) {
          throw new Error('Não foi possível gerar um código de indicação único');
        }
        setReferralCode(code);

        // Load referred users and check for confirmed sales
        const users = await getReferredUsers();
        setReferredUsers(users);

        // Check if user has any confirmed sales
        const hasSales = users.some(user => user.sales && user.sales.length > 0);
        setHasConfirmedSales(hasSales);

        // Calculate total commission from sales
        const totalCommission = users.reduce((sum, user) => sum + user.total_commission, 0);

        // Calculate total withdrawals from users data
        const totalWithdrawals = users.reduce((sum, user) => 
          sum + (user.withdrawals?.reduce((wSum, w) => wSum + w.amount, 0) || 0), 0);

        // Calculate available balance
        const availableBalance = totalCommission - totalWithdrawals;

        setBalanceSummary({
          totalCommission,
          totalWithdrawals,
          availableBalance
        });
      } catch (err) {
        console.error('Error loading referral data:', err);
        setError('Erro ao carregar dados. Por favor, tente novamente mais tarde.');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  const handleCopyCode = async () => {
    if (referralCode) {
      try {
        await navigator.clipboard.writeText(referralCode);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Error copying code:', err);
        alert('Erro ao copiar código');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center py-4">Carregando...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Flatten all sales into a single array
  const allSales = referredUsers.flatMap(user => user.sales);
  
  // Sort sales by date, most recent first
  const sortedSales = allSales.sort((a, b) => 
    new Date(b.sale_date).getTime() - new Date(a.sale_date).getTime()
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <Users className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">
            Programa de Indicações
          </h2>
        </div>

        <div className="space-y-6">
          {hasConfirmedSales && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="text-blue-600" size={20} />
                  <h3 className="font-medium text-blue-900">Total em Comissões</h3>
                </div>
                <p className="text-2xl font-bold text-blue-700">
                  {formatCurrency(balanceSummary.totalCommission)}
                </p>
              </div>

              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Wallet className="text-red-600" size={20} />
                  <h3 className="font-medium text-red-900">Total em Saques</h3>
                </div>
                <p className="text-2xl font-bold text-red-700">
                  {formatCurrency(balanceSummary.totalWithdrawals)}
                </p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="text-green-600" size={20} />
                  <h3 className="font-medium text-green-900">Saldo Disponível</h3>
                </div>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(balanceSummary.availableBalance)}
                </p>
              </div>
            </div>
          )}

          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-base font-medium text-blue-900 mb-2">
              Seja um parceiro da nossa agência de viagens e transforme suas indicações em experiências incríveis!
            </h3>
            <h3 className="text-base font-medium text-blue-900">
              <a 
                href="https://indicacoes.ftviagens.com.br/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                Clique aqui
              </a>{' '}
              e saiba como funciona
            </h3>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Seu código de indicação
            </h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={referralCode || ''}
                readOnly
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium"
              />
              <button
                onClick={handleCopyCode}
                className={`w-full sm:w-auto px-4 py-2 ${
                  copySuccess ? 'bg-green-600' : 'bg-blue-600'
                } text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2 whitespace-nowrap`}
              >
                <Share2 size={20} />
                <span>{copySuccess ? 'Copiado!' : 'Copiar'}</span>
              </button>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Vendas Realizadas
            </h3>
            {sortedSales.length > 0 ? (
              <div className="space-y-3">
                {sortedSales.map((sale, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-gray-50 rounded-lg flex justify-between items-center"
                  >
                    <div className="space-y-1">
                      <div className="font-medium text-gray-900">
                        {sale.customer_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(sale.sale_date)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-green-600">
                        {formatCurrency(sale.commission_value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-600">
                Você ainda não tem vendas registradas.
                <br />
                Compartilhe seu código e comece a ganhar comissões!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}